<template>
  <div class="player-metrics">
    <div v-if="bundle.isTimedOut.value" class="metrics-banner" role="alert">
      <span class="metrics-banner__icon" aria-hidden="true">&#9888;</span>
      <span>timeout of {{ bundle.timeoutMs }}ms exceeded</span>
    </div>

    <div v-if="bundle.error.value" class="error-text">{{ bundle.error.value }}</div>

    <div class="now-playing">
      <label>Currently Playing</label>
      <div v-if="nowPlayingList.length" class="now-playing-thumbs">
        <div class="np-item" v-for="name in nowPlayingList" :key="name">
          <img
            v-if="nowPlayingThumbMap[name]"
            :src="nowPlayingThumbMap[name]"
            class="np-thumb"
            :alt="name"
          />
          <div class="np-name">{{ name }}</div>
        </div>
      </div>
      <div v-else class="now-playing-value">&mdash;</div>
    </div>

    <div class="metrics-meta">
      <span v-if="bundle.isLoading.value" class="inline-spinner" />
      <span v-else-if="lastUpdatedText" class="timestamp" :title="tooltipText">{{ lastUpdatedText }}</span>
    </div>

    <div v-if="metrics" class="metrics-grid">
      <div class="metric">
        <div class="metric-header">
          <div class="metric-title-wrap">
            <span class="metric-title">Memory</span>
            <span
              v-if="memoryStatus !== 'good'"
              class="status-icon"
              :class="{ warn: memoryStatus==='warn', bad: memoryStatus==='bad' }"
              :title="memoryStatus==='warn' ? 'Warning' : 'Critical'"
            >&#9888;</span>
          </div>
          <span class="metric-value">{{ memUsedMb }} / {{ metrics.mem_total_mb }} MB</span>
        </div>
        <div class="bar">
          <div class="bar-fill mem" :class="{ grad: memoryStatus === 'warn' }" :style="{ width: memUsagePct + '%' }" />
        </div>
        <div class="bar-meta">{{ memUsagePct.toFixed(0) }}%</div>
      </div>

      <div class="metric">
        <div class="metric-header">
          <div class="metric-title-wrap">
            <span class="metric-title">CPU</span>
            <span
              v-if="cpuStatus !== 'good'"
              class="status-icon"
              :class="{ warn: cpuStatus==='warn', bad: cpuStatus==='bad' }"
              :title="cpuStatus==='warn' ? 'Warning' : 'Critical'"
            >&#9888;</span>
          </div>
          <span class="metric-value">{{ metrics.cpu_usage_pct.toFixed(1) }}%</span>
        </div>
        <div class="bar">
          <div class="bar-fill cpu" :class="{ grad: cpuStatus === 'warn' }" :style="{ width: cpuPct + '%' }" />
        </div>
        <div class="bar-meta">{{ clampPct(metrics.cpu_usage_pct).toFixed(0) }}%</div>
      </div>

      <div class="metric">
        <div class="metric-header">
          <div class="metric-title-wrap">
            <span class="metric-title">Disk</span>
            <span
              v-if="diskStatus !== 'good'"
              class="status-icon"
              :class="{ warn: diskStatus==='warn', bad: diskStatus==='bad' }"
              :title="diskStatus==='warn' ? 'Warning' : 'Critical'"
            >&#9888;</span>
          </div>
          <span class="metric-value">{{ diskUsedMb }} / {{ metrics.disk_total_mb }} MB</span>
        </div>
        <div class="bar">
          <div class="bar-fill disk" :class="{ grad: diskStatus === 'warn' }" :style="{ width: diskUsagePct + '%' }" />
        </div>
        <div class="bar-meta">{{ diskUsagePct.toFixed(0) }}%</div>
      </div>

      <div class="metric">
        <div class="metric-header">
          <div class="metric-title-wrap">
            <span class="metric-title">Temperature</span>
            <span
              v-if="tempStatus !== 'good'"
              class="status-icon"
              :class="{ warn: tempStatus==='warn', bad: tempStatus==='bad' }"
              :title="tempStatus==='warn' ? 'Warning' : 'Critical'"
            >&#9888;</span>
          </div>
          <span class="metric-value">{{ metrics.temperature_c.toFixed(1) }}&deg;C</span>
        </div>
        <div class="bar">
          <div class="bar-fill temp" :class="{ grad: tempStatus === 'warn' }" :style="{ width: tempPct + '%' }" />
        </div>
        <div class="bar-meta">{{ tempPct.toFixed(0) }}%</div>
      </div>
    </div>

    <div v-else-if="!bundle.isLoading.value && !bundle.error.value" class="no-data">No recent metrics</div>
  </div>
</template>

<script lang="ts" setup>
// @ts-nocheck
import { computed } from 'vue';

const props = defineProps<{ bundle: any }>();

const metrics = computed(() => props.bundle?.metrics?.value ?? null);
const lastUpdated = computed(() => props.bundle?.lastUpdated?.value ?? null);
const nowPlayingList = computed(() => props.bundle?.nowPlayingList?.value ?? []);
const nowPlayingThumbMap = computed(() => props.bundle?.nowPlayingThumbMap?.value ?? {});

function clampPct(value) {
  return Math.max(0, Math.min(100, value ?? 0));
}

const memUsedMb = computed(() => metrics.value ? Math.max(metrics.value.mem_total_mb - metrics.value.mem_free_mb, 0) : 0);
const memUsagePct = computed(() => metrics.value && metrics.value.mem_total_mb > 0
  ? (memUsedMb.value / metrics.value.mem_total_mb) * 100
  : 0);

const diskUsedMb = computed(() => metrics.value ? Math.max(metrics.value.disk_total_mb - metrics.value.disk_free_mb, 0) : 0);
const diskUsagePct = computed(() => metrics.value && metrics.value.disk_total_mb > 0
  ? (diskUsedMb.value / metrics.value.disk_total_mb) * 100
  : 0);

const cpuPct = computed(() => clampPct(metrics.value?.cpu_usage_pct));

function getStatusForPercent(value, thresholds) {
  const v = clampPct(value ?? 0);
  if (v < thresholds.warn) return 'good';
  if (v < thresholds.bad) return 'warn';
  return 'bad';
}

const memoryStatus = computed(() => getStatusForPercent(memUsagePct.value, { warn: 75, bad: 85 }));
const cpuStatus = computed(() => getStatusForPercent(cpuPct.value, { warn: 70, bad: 90 }));
const diskStatus = computed(() => getStatusForPercent(diskUsagePct.value, { warn: 70, bad: 90 }));
const tempStatus = computed(() => {
  const tempC = metrics.value?.temperature_c ?? 0;
  if (tempC < 70) return 'good';
  if (tempC < 85) return 'warn';
  return 'bad';
});

const tempPct = computed(() => {
  const temp = metrics.value?.temperature_c ?? 0;
  return clampPct((temp / 100) * 100);
});

const lastUpdatedText = computed(() => {
  if (!lastUpdated.value) return '';
  return `Updated ${lastUpdated.value.toLocaleTimeString()}`;
});

const tooltipText = computed(() => lastUpdated.value ? lastUpdated.value.toLocaleString() : '');
</script>

<style scoped>
.player-metrics {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.metrics-banner {
  /* BUI bad badge palette — rose hairline on a soft rose surface with orchid text. */
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--bui-badge-tag-fill, #ffd9e6);
  border: 1px solid var(--bui-badge-bad-fill, #de3da9);
  color: var(--bui-badge-bad-fill, #de3da9);
  border-radius: 8px;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  font-weight: 600;
}

.metrics-banner__icon {
  font-size: 1rem;
  line-height: 1;
  color: var(--bui-badge-bad-fill, #de3da9);
}

.metrics-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 14px;
}

.timestamp {
  color: var(--bui-tbl-detail-text, #5c5c66);
  font-size: 0.8rem;
}

.inline-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid var(--bui-tbl-line, #e8e8ec);
  border-top: 2px solid var(--bui-chk-checked-fill, #2a2a30);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-text {
  color: var(--bui-badge-bad-fill, #de3da9);
  margin: 0.25rem 0;
  font-weight: 500;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
}

.metric {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.metric-title-wrap {
  display: inline-flex;
  align-items: center;
}

.metric-title {
  font-weight: 600;
  color: var(--bui-tbl-row-text, #2a2969);
}

.metric-value {
  color: var(--bui-tbl-row-text, #2a2969);
  font-size: 0.85rem;
}

.bar {
  height: 10px;
  background: var(--bui-badge-neutral-fill, rgba(0, 0, 0, 0.06));
  border-radius: 9999px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.bar-fill.mem { background: #00FFFF; }
.bar-fill.mem.grad { background: linear-gradient(90deg, #00FFFF 0%, #69E7FF 100%); }
.bar-fill.cpu { background: #BAFF5C; }
.bar-fill.cpu.grad { background: linear-gradient(90deg, #BAFF5C 0%, #00CF67 100%); }
.bar-fill.disk { background: #6D76FF; }
.bar-fill.disk.grad { background: linear-gradient(90deg, #6D76FF 0%, #0C00AD 100%); }
.bar-fill.temp { background: #6B1AB1; }
.bar-fill.temp.grad { background: linear-gradient(90deg, #6B1AB1 0%, #A420C8 100%); }

.status-icon {
  margin-left: 0.25rem;
  font-size: 1rem;
  line-height: 1;
}
.status-icon.warn { color: var(--c-bui-c-warning, #ffc05c); }
.status-icon.bad { color: var(--bui-badge-bad-fill, #de3da9); }

.bar-meta {
  color: var(--bui-tbl-detail-text, #5c5c66);
  font-size: 0.8rem;
}

.now-playing {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.now-playing label {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--bui-tbl-row-text, #2a2969);
  margin: 0.2rem 0.25rem 0 0;
}

.now-playing-value {
  background: var(--bui-badge-neutral-fill, rgba(0, 0, 0, 0.06));
  border: 1px solid var(--bui-tbl-line, #e8e8ec);
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  color: var(--bui-tbl-row-text, #2a2a30);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.now-playing-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.np-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  background: var(--bui-badge-neutral-fill, rgba(0, 0, 0, 0.06));
  border: 1px solid var(--bui-tbl-line, #e8e8ec);
  border-radius: 6px;
  padding: 0.6rem 0.8rem;
}

.np-thumb {
  width: 100%;
  max-width: 256px;
  height: auto;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid var(--bui-tbl-line, #e8e8ec);
}

.np-name {
  font-size: 0.9rem;
  color: var(--bui-tbl-row-text, #2a2969);
  text-align: center;
  line-height: 1.25;
  max-width: 256px;
  width: 100%;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.no-data {
  color: var(--bui-tbl-detail-text, #5c5c66);
  font-style: italic;
}
</style>
